import { Suggestion, Suggestions } from 'types/suggestions';
import { trackingHelper } from 'utils/tracking-helper';
import { analytics, patchUrlByLocale } from 'utils';
import { ProductRedirectionEvents } from 'actions';
import { SEARCH_OVERRIDES } from './overrides';
import { SearchHistoryItem } from '../use-search-history-context';
import { handleRedirect } from './redirects';
import { navigate } from './navigate';

export const getSearchParams = (suggestion: Suggestion | null, inputValue: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append('q', inputValue);

  if (suggestion && 'phrase' in suggestion && suggestion.phrase) {
    searchParams.set('q', suggestion.phrase);
    searchParams.append('suggest', 'true');
  }
  return searchParams;
};

const isKzDomain = () => {
  const { host } = global?.location || { host: '' };
  return host.endsWith('.kz') || host.endsWith('lmkz.tech');
};

export const normalizeInput = (str: string) => {
  return str.toLowerCase().trim();
};

const getOverride = (input: string, selectedSuggestion: Suggestion | null): string | null => {
  const normalizedInput = normalizeInput(input);
  if (!selectedSuggestion && !isKzDomain() && SEARCH_OVERRIDES[normalizedInput]) {
    return SEARCH_OVERRIDES[normalizedInput];
  }
  return null;
};

export const handleSubmit = (
  e: React.FormEvent<HTMLFormElement>,
  value: string,
  sanitizedValue: string,
  isLmcode: boolean,
  productRedirectionUrl: string | null | undefined,
  productRedirectionUrlLoading: boolean | undefined,
  selectedSuggestion: Suggestion | null,
  currentRegionId: string | undefined,
  setIsSubmitted: (value: React.SetStateAction<boolean>) => void,
  addHistoryFromSubmit: (value: string, sanitizedValue: string, suggestion: Suggestion | null) => void,
  addHistoryItem: (item: SearchHistoryItem) => void,
  dispatch: (event: string, payload: unknown) => void,
  collapse: () => void,
  suggestions: Suggestions | null,
  suggestionsLoading?: boolean,
) => {
  e.preventDefault();
  setIsSubmitted(true);
  trackingHelper.dispatchSearchEvent(value);
  if (sanitizedValue) {
    analytics.sendEvent('GA:click', { position: 'search', name: value });
    analytics.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'header',
      elementName: 'search',
      elementText: value,
    });
    const override = getOverride(value, selectedSuggestion);
    if (!!override) {
      const patchedUrl = patchUrlByLocale({ url: override });
      navigate(patchedUrl);
      collapse();
      addHistoryItem({
        title: value,
        url: patchedUrl,
      });
      return;
    }

    if (selectedSuggestion) {
      handleSelectedSuggestion(selectedSuggestion, value, sanitizedValue, addHistoryFromSubmit, collapse);
      return;
    }

    const lmCodeRedirect = isLmcode && !productRedirectionUrl && !productRedirectionUrlLoading;

    if (lmCodeRedirect) {
      dispatch(ProductRedirectionEvents.request, { lmcode: sanitizedValue, regionId: currentRegionId });
      return;
    }

    const correctSuggestions = suggestions?.query === value;
    const hasRedirects = suggestions?.redirects && suggestions.redirects.length > 0;

    if (!suggestionsLoading && correctSuggestions && hasRedirects) {
      return handleRedirect(suggestions.redirects![0], collapse, addHistoryItem);
    }

    const searchParams = getSearchParams(selectedSuggestion, sanitizedValue);
    const searchUrl = patchUrlByLocale({ url: `/search/?${searchParams}` });
    addHistoryFromSubmit(value, sanitizedValue, selectedSuggestion);
    navigate(searchUrl);
    collapse();
    return;
  }
  // default list pinned items fix
  handleDefaultListPinnedItem(selectedSuggestion, collapse);
};

export const getSanitizedValue = (suggestion: Suggestion | null) => {
  if (!suggestion) return null;
  if ('phrase' in suggestion && suggestion.phrase) return suggestion.phrase;
  if ('name' in suggestion && suggestion.name) return suggestion.name;
  return null;
};

export const getCategoryUrl = (suggestion: Suggestion | null) => {
  if (!suggestion) return null;
  if ('categoryUrl' in suggestion && suggestion.categoryUrl) return suggestion.categoryUrl;
  if ('pageUrl' in suggestion && suggestion.pageUrl) return suggestion.pageUrl;
  return null;
};

const handleDefaultListPinnedItem = (suggestion: Suggestion | null, collapse: () => void) => {
  const categoryUrl = getCategoryUrl(suggestion);
  if (categoryUrl) {
    navigate(patchUrlByLocale({ url: categoryUrl }));
    collapse();
  }
};

const handleSelectedSuggestion = (
  selectedSuggestion: Suggestion,
  value: string,
  sanitizedValue: string,
  addHistoryFromSubmit: (value: string, sanitizedValue: string, suggestion: Suggestion | null) => void,
  collapse: () => void,
) => {
  const categoryUrl = getCategoryUrl(selectedSuggestion);
  if (categoryUrl) {
    addHistoryFromSubmit(value, sanitizedValue, selectedSuggestion);
    navigate(patchUrlByLocale({ url: categoryUrl }));
    collapse();
    return;
  }
  const searchParams = getSearchParams(selectedSuggestion, sanitizedValue);
  const searchUrl = patchUrlByLocale({ url: `/search/?${searchParams}` });
  addHistoryFromSubmit(value, sanitizedValue, selectedSuggestion);
  navigate(searchUrl);
  collapse();
};
